import React, { useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import styles from './RequestBreeding.module.scss';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../Auth';
import PageTable from '../../Shared/PageTable/PageTable';
import { general } from '../../../general';
import { StepCell, UserSelectCell, useBreedUsers, TextCell } from '../../Shared/SchemaFieldCells/SchemaFieldCells';

function RequestBreeding(props: RequestBreedingProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();


    // Fanen ligger i URL-en, ikke i lokal state. Da overlever den at man klikker
    // inn på en rad og trykker tilbake — historikkoppføringen man kommer tilbake
    // til bærer ?tab= — og fanen kan deles som lenke. replace: true holder
    // fanebytter utenfor historikken, så tilbakeknappen forlater siden i stedet
    // for å gå bakover gjennom faner man har klikket på.
    const tabKeys = ["new", "accepted", "rejected", "ongoing"];
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get("tab");
    const activeTab = tabParam != null && tabKeys.includes(tabParam) ? tabParam : "new";

    function setActiveTab(key: string) {
        const params = new URLSearchParams(searchParams);
        params.set("tab", key);
        setSearchParams(params, { replace: true });
    }

    // edit: "user" er nedtrekk fra brukerlisten (lagrer bruker-ID, med gammel
    // fritekst i legacyField), "step" er avhuking for et steg i prosessen, og
    // "text" er fritt tekstfelt.
    const requestFields = [
        { field: "TIMESTAMP" },
        { field: "dogName" },
        { field: "ownerName" },
        { field: "CONTROLLER_USER_ID", edit: "user", legacyField: "CONTROLLER" },
        { field: "RESPONSIBLE_USER_ID", edit: "user", legacyField: "RESPONSIBLE" },
        { field: "STEP_CONTROLLER_APPROVED", edit: "step" },
        { field: "STEP_APPROVAL_SENT", edit: "step" },
        { field: "STEP_SOCIAL_MEDIA", edit: "step" },
        { field: "STEP_PUPPY_LIST", edit: "step" },
        { field: "PROCESS_STATUS", edit: "text" },
    ];

    const breedUsers = useBreedUsers();
    
    const ongoingFields = [
        { field: "CREATEDTIME", editable: false },
        //"DAM_REGNO", Not implemented in query
        { field: "DAM_ANIMAL_NAME", editable: false },
        { field: "SIRE_ANIMAL_NAME", editable: false },
        { field: "DAM_OWNER_NAME", editable: false },
        { field: "STATUS", editable: false },
        { field: "PUBLISHED_PUBLIC", editable: false },
    ];

    function mapFieldToDataAnswer(field: string) {
        let fieldId = 0;
        switch (field) {
            case "dogName": fieldId = 1; break;
        }
        return (data: any) => {
            return data.DATA.find((d: any) => d.TRAIT_ID == fieldId)?.answer;
        }
    }

    function createTraitColumn(trait: any) {
        if (trait != undefined) {
            let column: any = {
                label: t("requestbreeding.fields." + trait.field),
                field: trait.field,
            };

            if (trait.field == "TIMESTAMP" || trait.field == "CREATEDTIME") {
                column["override"] = (data: any) => {
                    return new Date(data.TIMESTAMP || data.CREATEDTIME).toLocaleString("no-NO");
                }
            }

            switch (trait.field) {
                case "dogName":
                    column["override"] = mapFieldToDataAnswer(trait.field);
                    break;
                case "ownerName":
                    // Backend henter eiernavnet fra skjemasvaret med
                    // CUSTOMTRAIT_ID 2. Det er et annet ID-rom enn TRAIT_ID,
                    // så oppslaget i DATA over traff aldri.
                    column["override"] = (data: any) => data.OWNERNAME;
                    break;
                case "STATUS":
                    column["override"] = (data: any) => {
                        return t("requestbreeding.statuses." + data.STATUS);
                    }
                    column["customFilters"] = [
                        { label: t("requestbreeding.statuses.awaiting"), value: "awaiting" },
                        { label: t("requestbreeding.statuses.mated"), value: "mated" },
                        { label: t("requestbreeding.statuses.attempted"), value: "attempted" },
                        { label: t("requestbreeding.statuses.failed"), value: "failed" },
                        { label: t("requestbreeding.statuses.successful"), value: "successful" },
                    ];
                    break;
                case "DAM_OWNER_NAME":
                    column["override"] = (data: any) => {
                        return data.DAM_OWNER_NAME == ", " ? "" : data.DAM_OWNER_NAME;
                    }
                    break;
                case "PUBLISHED_PUBLIC":
                    column["override"] = (data: any) => {
                        return data.PUBLISHED_PUBLIC != "0" && data.PUBLISHED_PUBLIC != null ? t("requestbreeding.fields.published") : t("requestbreeding.fields.notpublished");
                    }
                    column["customFilters"] = [
                        { label: t("requestbreeding.fields.published"), value: "1" },
                        { label: t("requestbreeding.fields.notpublished"), value: "0" },
                    ];
                    break;
            }
            
            if (trait.edit == "user") {
                column["override"] = (data: any) => (
                    <UserSelectCell row={data} field={trait.field} legacyField={trait.legacyField} users={breedUsers} />
                );
            }

            if (trait.edit == "step") {
                column["override"] = (data: any) => (
                    <StepCell row={data} field={trait.field} label={t("requestbreeding.fields." + trait.field)} />
                );
            }

            if (trait.edit == "text") {
                column["override"] = (data: any) => (
                    <TextCell row={data} field={trait.field} label={t("requestbreeding.fields." + trait.field)} />
                );
            }

            return column;
        } else {
            return undefined;
        }
    }

    function createRequestColumns() {
        let columns: any[] = [];
        requestFields.forEach((trait: any) => {
            columns.push({
                ...createTraitColumn(trait),
                noFilter: true,
            });
        });
        
        return columns;
    }

    function createOngoingColumns() {
        let columns: any[] = [];
        ongoingFields.forEach((trait: any) => {
            columns.push(createTraitColumn(trait));
        });
        
        return columns;
    }
            
    useEffect(() => {
        if (
            (
                auth.user?.userdata?.ROLE != "admin" &&
                auth.user?.userdata?.ROLE != "sysadmin" &&
                auth.user?.userdata?.ROLE != "user"
            ) ||
            general.getBreedAccess(auth.user, auth.selectedBreed) == "dogowner"
        ) {
            navigate("/");
        }
    }, [auth.user?.userdata?.ROLE, auth.selectedBreed]);

    return (
        <div className="maincontent">
            <div className={styles.dogs}>
                <h1>{t("requestbreeding.title")}</h1>
                <div
                    className={styles.configbutton}
                >
                    <button
                        onClick={() => {
                            navigate("/partnersoknader/config");
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-gear-fill" viewBox="0 0 16 16">
                            <path d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311c.446.82.023 1.841-.872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1 .872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872-2.105l.34-.1c1.4-.413 1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872zM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858z"/>
                        </svg>
                    </button>
                </div>
                <div className={styles.tabs}>
                    <div className={styles.tabList}>
                        {[
                            { key: "new",      label: t("requestbreeding.filters.status.new") },
                            { key: "accepted", label: t("requestbreeding.filters.status.accepted") },
                            { key: "rejected", label: t("requestbreeding.filters.status.rejected") },
                            { key: "ongoing",  label: t("requestbreeding.areas.ongoing.title") },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tabUnderline} />
                </div>
                {(activeTab === "new" || activeTab === "accepted" || activeTab === "rejected") && auth.selectedBreed != undefined &&
                    <PageTable
                        dataUrl={"/api/schema/getAllPaged/" + auth.selectedBreed}
                        // Radene har redigerbare celler, så de må nøkles på skjema-ID —
                        // ellers beholder inputene forrige fanes verdier (se PageTable).
                        rowKey={(dataLine: any) => dataLine.ID}
                        saveFiltersKey={"requestbreeding.table." + auth.selectedBreed}
                        onLineClick={(dataLine: any) => {
                            // Ta med fanen inn i detaljsida, så tilbake-knappen der
                            // treffer riktig fane.
                            navigate("/partnersoknader/" + dataLine.SCHEMA_KEY + "/" + dataLine.ID, {
                                state: { listSearch: location.search },
                            });
                        }}
                        options={{
                            showFields: [
                                ...createRequestColumns(),
                            ],
                        }}
                        permaFilters={[
                            {
                                field: "BREED_ID",
                                exact: false,
                                type: "and",
                                values: [auth.selectedBreed?.toString() || ""]
                            },
                            {
                                field: "STATUS",
                                exact: false,
                                type: "and",
                                values: [activeTab]
                            },
                            {
                                field: "SCHEMA_KEY",
                                exact: false,
                                type: "and",
                                values: ["malepartnerreq"]
                            }
                        ]}
                        selectedTraits={requestFields}
                        clickable
                        defaultSortDirection="DESC"
                        defaultSortField="TIMESTAMP"
                        hidePagination={false}
                        hidePagesize={false}
                        hideFilter={false}
                    />
                }
                {activeTab === "ongoing" &&
                    <PageTable
                        dataUrl="/api/breedings/getAllPaged"
                        saveFiltersKey={"requestbreeding.ongoing.table." + auth.selectedBreed}
                        onLineClick={(dataLine: any) => {
                            navigate("/openbreedings/" + dataLine.ID);
                        }}
                        options={{
                            showFields: [
                                ...createOngoingColumns(),
                            ],
                        }}
                        permaFilters={[
                            {
                                field: "BREED_ID",
                                exact: false,
                                type: "and",
                                values: [auth.selectedBreed?.toString() || ""]
                            },
                            {
                                field: "STATUS",
                                exact: true,
                                type: "not",
                                values: ["disabled"]
                            },
                        ]}
                        selectedTraits={ongoingFields}
                        clickable
                        defaultSortDirection="DESC"
                        defaultSortField="CREATEDTIME"
                        hidePagination={false}
                        hidePagesize={false}
                        hideFilter={false}
                    />
                }
            </div>
        </div>
    );
}

class RequestBreedingProps {
    
}

export default RequestBreeding;